import { Icon } from "@iconify/react"
import { Button, Card, Col, Flex, Form, Grid, Input, message, Modal, Pagination, Row, Select, Space, Table, Tag, Typography } from "antd"
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { complaintService } from "../../services/complaint.service";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import useDebounce from "../../hooks/useDebounce";
import pallete from "../../utils/pallete";
import { downloadBlob } from "../../utils/download";

// eslint-disable-next-line react/prop-types
const StatisticCard = ({ title, count }) => {
  return (
    <Card
      style={{ backgroundColor: pallete.grey[200], border: 0 }}
      styles={{
        body: {
          padding: 16
        }
      }}
    >
      <Space direction="vertical" >
        <Typography.Text>
          {title}
        </Typography.Text>
        <Typography.Title level={4} style={{ margin: 0 }} >
          {count}
        </Typography.Title>
      </Space>
    </Card>
  )
}

export default function ComplaintCustomer(){

  const breakpoint = Grid.useBreakpoint()

  const [filterForm] = Form.useForm()

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();
  const [messageApi, messageHolder] = message.useMessage()
  
  const [keyword, setKeyword] = useState("");
  const [exportModal, setExportModal] = useState(false)
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    query: null,
    status: null,
    source: null
  })

  const { data: complaintData , isLoading, isRefetching } = useQuery({
    queryKey: ['complaints', dataParams],
    queryFn: () => complaintService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const { data: summaryData } = useQuery({
    queryKey: ['complaintsSummary'],
    queryFn: () => complaintService.getSummary({ type: 'customer' }),
    select: ({ data }) => data
  })

  const exportMutation = useMutation({
    mutationFn: (val) => complaintService.export(val),
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename)
      messageApi.success("Data berhasil diexport")
    }
  })

  useEffect(() => {
    setTitle("Pengaduan - Kustomer");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      query: debouncedKeyword
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const handleExport = (values) => exportMutation.mutate(values)

  const handleCloseModal = () => {
    setExportModal(false)
    filterForm.resetFields()
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      render: (text, record, index) =>  index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Pertanyaan',
      width: 210,
      fixed: breakpoint.md && 'left',
      render: record => (
        <Space direction="vertical" size={2} >
          <Typography.Text style={{ marginBottom: 0, fontWeight: 600 }} >
            {record.nomen}
          </Typography.Text>
          <Typography.Text type="secondary" >
            {record.name}
          </Typography.Text>
        </Space>
      )

    },
    {
      title: 'Register ID',
      dataIndex: 'registerId',
      width: 180,
    },
    {
      title: 'CASE ID',
      dataIndex: 'caseId',
      width: 180,
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      width: 150,
    },
    {
      title: 'Area Bisnis',
      dataIndex: 'ab',
      width: 180,
    },
    {
      title: 'Sumber',
      dataIndex: 'source',
      width: 180,
    },
    {
      title: 'Tanggal Aduan',
      width: 180,
      render: record => (
        <Space direction="vertical" size={2} >
          <Typography.Text style={{ marginBottom: 0, fontWeight: 400 }} >
            {record.date}
          </Typography.Text>
          <Typography.Text type="secondary" >
            {record.time}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Kategori',
      dataIndex: 'categories',
      width: 150,
      render: val => val?.map((e,i ) => <Tag style={{ margin: 4 }} key={i} color="cyan" >{e}</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      align: 'center',
      width: 90,
      render: val => {
        switch (val) {
          case 'accepted':
            return <Tag>Diterima</Tag>
          case 'process':
            return <Tag color="cyan" >Diproses</Tag>
          case 'reject':
            return <Tag color="red" >Ditolak</Tag>
          case 'finish':
            return <Tag color="green" >Selesai</Tag>
        }
      },
      fixed: 'right',
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <Button  
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate('/complaint/detail/'+record.id)} 
          size=""
        />
      )
    },
  ]

  return (
    <>
      {messageHolder}
      <Card style={{ marginBottom: 20 }} >
        <Typography.Title level={5} style={{ marginBottom: 20 }} >
          Metrik Status Pengaduan
        </Typography.Title>
        <Row gutter={[20, 20]} >
          <Col sm={6} xs={12} >
            <StatisticCard
              title={'Laporan Diterima'}
              count={summaryData?.accept}
            />
          </Col>
          <Col sm={6} xs={12} >
            <StatisticCard
              title={'Laporan Diproses'}
              count={summaryData?.process}
            />
          </Col>
          <Col sm={6} xs={12} >
            <StatisticCard
              title={'Laporan Ditolak'}
              count={summaryData?.reject}
            />
          </Col>
          <Col sm={6} xs={12} >
            <StatisticCard
              title={'Laporan Selesai'}
              count={summaryData?.finish}
            />
          </Col>
        </Row>
      </Card>
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0
          }
        }}
      >
        <Flex align="center" gap={20} style={{ paddingInline: 24, marginBottom: 24 }} >
          <Input 
            style={{ width: 320 }} 
            prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
            placeholder="cari..."
            onChange={e => setKeyword(e.target.value)}
            value={keyword}
          />
          <Select
            placeholder="Status"
            style={{ width: 180 }}
            onChange={(e) => setDataParams({ ...dataParams, status: e })}
            allowClear
            options={[
              {
                label: 'Diproses',
                value: 'process'
              },
              {
                label: 'Diterima',
                value: 'accepted'
              },
              {
                label: 'Ditolak',
                value: 'rejected'
              },
              {
                label: 'Selesai',
                value: 'finish'
              },
            ]}
          />
          <Select
            style={{ width: 180 }}
            placeholder="Sumber"
            onChange={(e) => setDataParams({ ...dataParams, source: e })}
            allowClear
            options={[
              {
                label: 'Super App',
                value: 'SUPER_APP'
              },
              {
                label: 'Website',
                value: 'WEBSITE'
              }
            ]}
          />
          <Button 
            variant="solid" 
            onClick={() => setExportModal(true)}
            style={{ marginLeft: 'auto' }}
            color="primary" 
            icon={
              <Icon
                icon={'hugeicons:file-export'}
                width={18}
                height={18}
                style={{ marginLeft: -5, marginBottom: -2 }}
              />
            }
          >
            Export
          </Button>
        </Flex>
        <Table
          columns={columns}
          pagination={false}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={'id'}
          scroll={{ y: tableHeight }}
          dataSource={complaintData?.data}
        />
        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={complaintData?.pagination?.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={['10', '20', '50']}
          current={complaintData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
      <Modal
        open={exportModal}
        title="Export Data"
        centered
        styles={{
          body: {
            paddingTop: 14,
            paddingBottom: 14,
          }
        }}
        onOk={filterForm.submit}
        onCancel={handleCloseModal}
      >
        <Form
          form={filterForm}
          colon={false}
          labelCol={{ span: 4 }}
          labelAlign="left"
          onFinish={handleExport}
        >
          <Form.Item rules={[{ required: true }]} name={'start'} label="Mulai" >
            <Input type="date" />
          </Form.Item>
          <Form.Item 
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const start = getFieldValue('start');
                  if (!value || !start || value > start) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("Tanggal sampai harus lebih besar dari tanggal mulai")
                  );
                },
              }),
            ]} 
            label="Sampai" 
            name={'end'} 
          >
            <Input type="date" />
          </Form.Item>
          <Form.Item label="Status" name={'status'} initialValue={'Semua'} >
            <Select
              options={['Semua', 'Diterima', 'Diproses', 'Ditolak', 'Selesai'].map(e => ({ label: e, value: e }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}